import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import deepEqual from 'fast-deep-equal';
import { ReactNode, useEffect } from 'react';
import { FormProvider, Path } from 'react-hook-form';
import { FormLayout, FormRow as Row, FormSection as Section } from '~/components';
import { WizardStepsProps } from '~/components/Wizard/types';
import { Wizard } from '~/components/Wizard/Wizard';
import { FacilityFormValues } from '~/features/types';
import { useServerWarnings } from '~/hooks/useServerWarnings';
import { useWizardSteps } from '~/hooks/useWizardSteps';
import {
  AsyncCreatableSelect,
  Checkbox,
  CreatableSelect,
  DatePicker,
  FormDialog,
  TextArea,
  TextInput,
  Toggle,
} from '../../../components';
import { useCustomForm } from '../../../hooks/useCustomForm';
import { DuplicateFacilityDialog, emptyFacilityForm } from '../facility';
import { API_CALLS as FACILITY_API_CALLS } from '../facility/api';
import { FacilityForm } from '../facility/FacilityForm';
import { APPOINTMENT_OPTIONS } from './constants';
import { CreateEmploymentFormValues, EmploymentFormValues, employmentSchema } from './types';

const TAGS = CT.employment.form;

const VALID_FIELDS: (keyof EmploymentFormValues)[] = ['startDate', 'endDate'];
const STEP_FIELDS: Path<EmploymentFormValues>[][] = [
  ['facility'],
  ['employer', 'title', 'department', 'apptType'],
  [
    'currentFlag',
    'startDate',
    'endDate',
    'privilegesHeldCheckbox',
    'rehireToggle',
    'rehireEligibleComment',
  ],
];

interface FormProps {
  addEmployment: (employment: CreateEmploymentFormValues) => void;
  editEmployment: (employment: EmploymentFormValues) => void;
  handleClose: () => void;
  openFacilityModal: boolean;
  facilityForm: Partial<FacilityFormValues>;
  create: boolean;
  form: Partial<EmploymentFormValues>;
  handleFacilityFormSubmit: (values: FacilityFormValues) => void;
  handleFacilityOpen: (values: FacilityFormValues, prefillName?: string) => void;
  handleFacilityClose: () => void;
  duplicates: FacilityFormValues[];
  showDuplicates: boolean;
  pendingFacilityName: string;
  onSelectExistingFacility: (facility: FacilityFormValues) => void;
  onConfirmCreateFacility: () => void;
  onBackToFacilityForm: () => void;
}

export const EmploymentForm = (props: FormProps) => {
  const {
    form,
    create,
    addEmployment,
    editEmployment,
    handleClose,
    openFacilityModal,
    facilityForm,
    handleFacilityFormSubmit,
    handleFacilityOpen,
    handleFacilityClose,
    duplicates,
    showDuplicates,
    pendingFacilityName,
    onSelectExistingFacility,
    onConfirmCreateFacility,
    onBackToFacilityForm,
  } = props;

  const methods = useCustomForm<EmploymentFormValues>({
    resolver: zodResolver(employmentSchema),
    defaultValues: form,
  });

  const { handleSubmit, watch, formState, resetField, getValues } = methods;

  useServerWarnings(methods, form.warnings, VALID_FIELDS);

  const onSubmitSuccess = (values: EmploymentFormValues | CreateEmploymentFormValues) => {
    if (create) {
      addEmployment(values as CreateEmploymentFormValues);
    } else {
      editEmployment(values as EmploymentFormValues);
    }
  };

  const onSubmitError = (err: unknown) => {
    console.debug('EmploymentForm Submit Error:', err);
  };

  useEffect(() => {
    if (!deepEqual(formState.defaultValues?.facility, form.facility)) {
      resetField('facility', { defaultValue: form.facility });
    }
  }, [form.facility]);

  const currentFlag = watch('currentFlag');
  const { currentStep, goBack, goNext, navigationInProgress } = useWizardSteps({
    methods,
    stepFields: STEP_FIELDS,
  });

  useEffect(() => {
    if (currentFlag) {
      resetField('endDate', { defaultValue: '' });
    }
  }, [currentFlag]);

  const wizardSteps: WizardStepsProps[] = [
    {
      header: 'Facility',
      label: 'Facility',
      cyTag: `${TAGS.form}-facility-step`,
      footer: {
        secondaryButton: {
          label: 'Cancel',
          cyTag: TAGS.cancelBtn,
          onClick: handleClose,
        },
        primaryButton: {
          label: 'Next',
          cyTag: `${TAGS.submitBtn}-next-facility`,
          disabled: navigationInProgress,
          onClick: goNext,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <Section componentsPerLine={1}>
            <Row>
              <AsyncCreatableSelect
                name={'facility'}
                cyTag={TAGS.facilityNameSelect}
                label={'Facility'}
                cacheOptions={true}
                loadOptions={FACILITY_API_CALLS.get}
                required
                noOptionsMessage="No matching facility — keep typing to add your own"
                // [MCV-1638] The add affordance is the in-menu `Add "<text>"` row
                // now, matching Education's institution picker, rather than the
                // standalone "Add a Facility" button that used to sit under this
                // field. Passing the typed text as prefillName opens the dialog
                // with the name already filled in, so choosing `Add "St Jude"`
                // does not throw away what was just typed.
                onCreateOption={(inputValue: string) =>
                  handleFacilityOpen(getValues('facility'), inputValue)
                }
              />
            </Row>
          </Section>
          {footer}
        </>
      ),
    },
    {
      header: 'Position',
      label: 'Position',
      cyTag: `${TAGS.form}-position-step`,
      footer: {
        backButton: {
          label: 'Back',
          cyTag: `${TAGS.submitBtn}-back-position`,
          onClick: goBack,
        },
        secondaryButton: {
          label: 'Cancel',
          cyTag: TAGS.cancelBtn,
          onClick: handleClose,
        },
        primaryButton: {
          label: 'Next',
          cyTag: `${TAGS.submitBtn}-next-position`,
          disabled: navigationInProgress,
          onClick: goNext,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <>
          <Section componentsPerLine={1}>
            <Row>
              <TextInput
                name={'employer'}
                cyTag={TAGS.employmentNameInput}
                label={'Employer Name*'}
              />
            </Row>
          </Section>
          <Section componentsPerLine={3}>
            <Row>
              <TextInput name={'title'} cyTag={TAGS.titleInput} label={'Title'} />
            </Row>
            <Row>
              <TextInput name={'department'} cyTag={TAGS.departmentInput} label={'Department'} />
            </Row>
            <Row>
              <CreatableSelect
                name={'apptType'}
                cyTag={TAGS.appointmentSelect}
                label={'Appointment Type'}
                options={APPOINTMENT_OPTIONS}
              />
            </Row>
          </Section>
          {footer}
        </>
      ),
    },
    {
      header: 'Dates & Notes',
      label: 'Dates',
      cyTag: `${TAGS.form}-dates-step`,
      footer: {
        backButton: {
          label: 'Back',
          cyTag: `${TAGS.submitBtn}-back-dates`,
          onClick: goBack,
        },
        secondaryButton: {
          label: 'Cancel',
          cyTag: TAGS.cancelBtn,
          onClick: handleClose,
        },
        primaryButton: {
          label: 'Save',
          cyTag: TAGS.submitBtn,
          isSubmit: true,
        },
      },
      children: ({ footer }: { footer: ReactNode }) => (
        <div className="flex flex-col gap-5">
          <Section componentsPerLine={1}>
            <Toggle
              name={'currentFlag'}
              cyTag={TAGS.currentToggle}
              label="Do you currently work here?"
            />
          </Section>
          <Section componentsPerLine={2}>
            <Row>
              <DatePicker name={'startDate'} cyTag={TAGS.startDatePicker} label={'Start Date*'} />
            </Row>
            <Row>
              <DatePicker
                name={'endDate'}
                cyTag={TAGS.endDatePicker}
                label={'End Date'}
                disabled={currentFlag}
                required={!currentFlag}
              />
            </Row>
          </Section>
          <div className="flex items-start border-b-2 font-bold pb-2 mt-2">
            <h2 className="text-lg bold">Additional Details</h2>
          </div>
          <Section componentsPerLine={1}>
            <div className="flex gap-8">
              <Checkbox
                name="privilegesHeldCheckbox"
                label="Privileges Held"
                cyTag={TAGS.privilegesHeldCheckbox}
                rowReverse
              />
              <Checkbox
                name="rehireToggle"
                label="Rehire Eligible"
                cyTag={TAGS.rehireEligibleToggle}
                rowReverse
              />
            </div>
          </Section>
          <Section componentsPerLine={1}>
            <Row>
              <TextArea
                cyTag={TAGS.reasonForLeaving}
                name="rehireEligibleComment"
                label={'Reason for leaving'}
                rows={4}
              />
            </Row>
          </Section>
          {footer}
        </div>
      ),
    },
  ];

  return (
    <>
      <FormProvider {...methods}>
        <form
          data-cy={TAGS.form}
          onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
          id="training-form"
        >
          <FormLayout>
            <Wizard steps={wizardSteps} currentStep={currentStep} />
          </FormLayout>
        </form>
      </FormProvider>
      <FormDialog
        title={'Add New Facility'}
        editing={false}
        creating={openFacilityModal}
        onClose={handleFacilityClose}
      >
        <FacilityForm
          facilityForm={facilityForm || emptyFacilityForm}
          handleFacilityFormSubmit={handleFacilityFormSubmit}
        />
      </FormDialog>
      <DuplicateFacilityDialog
        open={showDuplicates}
        duplicates={duplicates}
        pendingName={pendingFacilityName}
        onSelectExisting={onSelectExistingFacility}
        onConfirmCreate={onConfirmCreateFacility}
        onBack={onBackToFacilityForm}
      />
    </>
  );
};
